import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { AppErrorBoundary } from '@core/errors/AppErrorBoundary';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@core/api/auth.api';
import { permissionApi } from '@core/api/permission.api';
import { qk } from '@core/queries/keys';
import { useAuthStore } from '@core/stores/auth.store';
import { usePermissionsHydrated } from '@core/permissions/usePermission';
import { useUiPrefsStore } from '@core/stores/uiPrefs.store';
import { useIsMobileLayout } from '@core/hooks/useMediaQuery';
import { notify } from '@core/notify/toast';
import { FullPageSpinner, LoadingState } from '@ui/Spinner';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

/**
 * Authenticated app shell. Ports `core/shared/main-layout/`.
 *
 * PERMISSIONS ARE LOADED HERE, EXACTLY ONCE. The Angular app called
 * `loadPermissions()` from both `AppComponent.ngOnInit` and `HeaderComponent.ngOnInit`,
 * so every boot fired two identical requests. `staleTime: Infinity` keeps it to one per
 * session.
 *
 * Because the permission store is persisted, `hydrated` is already true on a refresh and
 * the shell renders immediately with correct nav — no flash of missing nav items, which
 * the in-memory Angular store could not avoid. The spinner only appears on a genuine
 * first load.
 */
export function AppLayout() {
  const navigate = useNavigate();
  // Keys the content error boundary, so navigating away clears a crashed page.
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const isLoggingOut = useAuthStore((state) => state.isLoggingOut);
  const localLogout = useAuthStore((state) => state.localLogout);
  const sidebarOpen = useUiPrefsStore((state) => state.sidebarOpen);
  const hydrated = usePermissionsHydrated();

  /*
   * ============================================================================
   * PHONES: the sidebar is a slide-over menu, opened from the header's ☰.
   * ============================================================================
   *
   * It used to stay on screen as a 64px icon rail at every width, leaving a phone ~310px for the
   * page. On a phone it is now hidden until asked for.
   *
   * ⚠️ OPEN FOR ONE PAGE, NOT A FLAG. The menu remembers the path it was opened on, so choosing a page
   * closes it with no effect to run — the new path simply is not the one it was opened on. Separate
   * from `sidebarOpen`, the desktop expand/collapse preference, which a phone must not overwrite.
   */
  const isMobile = useIsMobileLayout();
  const [mobileNavPath, setMobileNavPath] = useState<string | null>(null);
  const mobileNavOpen = isMobile && mobileNavPath === pathname;
  const openMobileNav = () => setMobileNavPath(pathname);
  const closeMobileNav = () => setMobileNavPath(null);

  // Esc closes it, as it does every other overlay in the app.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavPath(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  const { isLoading: permissionsLoading } = useQuery({
    queryKey: qk.permissions(),
    queryFn: () => permissionApi.loadPermissions(),
    enabled: isAuthenticated,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const logout = useMutation({
    mutationFn: () => authApi.logout(),
    // Angular cleared storage and navigated on BOTH success and error
    // (auth.service.ts:104-114) — a failed logout request must not trap the user.
    onSettled: () => {
      localLogout();
      queryClient.clear();
      notify.success('Logged out successfully');
      void navigate('/login', { replace: true });
    },
  });

  if (!hydrated && permissionsLoading) {
    return <FullPageSpinner message="Loading workspace…" />;
  }

  return (
    <div className="vm-shell">
      <Header
        onLogout={() => logout.mutate()}
        isLoggingOut={isLoggingOut || logout.isPending}
        onOpenNav={openMobileNav}
      />

      <div className="vm-layout">
        <Sidebar mobileOpen={mobileNavOpen} onCloseMobile={closeMobileNav} />
        {/* Tapping beside the open menu closes it. Phone only — see `.vm-nav-backdrop`. */}
        {mobileNavOpen && <div className="vm-nav-backdrop" aria-hidden onClick={closeMobileNav} />}
        <main className="vm-main" data-collapsed={!sidebarOpen}>
          {/*
            Two boundaries, both scoped to the CONTENT AREA rather than the whole app.

            Suspense: every routed page is `React.lazy` (see `router.tsx`), so one is required. Placed
            here, the header and sidebar stay mounted while a page chunk downloads — a navigation shows
            a spinner in the content area instead of blanking the shell.

            Error: `AppErrorBoundary` also wraps the app root in `providers.tsx`, which catches crashes
            in the shell itself. But with only that one, a render error inside any page destroyed the
            header and sidebar too, leaving no way out but a manual reload. This inner boundary keeps
            the shell alive and confines the crash screen to the page.

            `key={pathname}` remounts it on navigation, so a crashed page does not keep showing its
            error after the user has navigated somewhere else.
          */}
          <AppErrorBoundary key={pathname}>
            <Suspense fallback={<LoadingState message="Loading…" />}>
              <Outlet />
            </Suspense>
          </AppErrorBoundary>
        </main>
      </div>
    </div>
  );
}
